/**
 * Helper: Filters
 * -----------------------------------------------------------------------------
 * Handles collection/search filters.
 * - Build active filters from filters parameters.
 * - Build search parameters from active filters.
 *
 */

/**
 * Build active filters from filters parameters.
 * @param {Array} params - filter parameters.
 * @returns {Array} filters - active filters.
 */
export function setFiltersFromParams(params) {
  const filters = []

  params.forEach((param) => {
    if (param.key === 'filter.v.availability') {
      filters.push({
        available: param.value === '1',
      })

      return
    }

    if (param.key === 'filter.v.price.gte') {
      const priceFilter = filters.find((filter) => {
        return filter.price
      })

      if (!priceFilter) {
        filters.push({
          price: {
            min: Number(param.value),
          },
        })
        return
      }

      priceFilter.price.min = Number(param.value)
      return
    }

    if (param.key === 'filter.v.price.lte') {
      const priceFilter = filters.find((filter) => {
        return filter.price
      })

      if (!priceFilter) {
        filters.push({
          price: {
            max: Number(param.value),
          },
        })
        return
      }

      priceFilter.price.max = Number(param.value)
      return
    }

    if (param.key.includes('filter.p.m.')) {
      const keyParts = param.key.split('.')
      const metafieldKey = keyParts.pop()
      const metafieldNamespace = keyParts.pop()
      filters.push({
        productMetafield: {
          namespace: metafieldNamespace,
          key: metafieldKey,
          value: param.value,
        },
      })
      return
    }

    if (param.key === 'filter.p.product_type') {
      filters.push({
        productType: param.value,
      })
      return
    }

    if (param.key === 'filter.p.vendor') {
      filters.push({
        productVendor: param.value,
      })
      return
    }

    if (param.key === 'filter.p.tag') {
      filters.push({
        tag: param.value,
      })
      return
    }

    if (param.key.includes('filter.v.m.')) {
      const keyParts = param.key.split('.')
      const metafieldKey = keyParts.pop()
      const metafieldNamespace = keyParts.pop()
      filters.push({
        variantMetafield: {
          namespace: metafieldNamespace,
          key: metafieldKey,
          value: param.value,
        },
      })
      return
    }

    if (param.key.includes('filter.v.option.')) {
      const keyParts = param.key.split('.')
      const optionName = keyParts.pop()
      filters.push({
        variantOption: {
          name: optionName,
          value: param.value,
        },
      })
    }
  })

  return filters
}

/**
 * Build search parameters from active filters.
 * @param {Array} params - search parameters.
 * @param {Array} filters - active filters.
 */
export function setParamsFromFilters(params, filters) {
  filters.forEach((filter) => {
    const firstKey = Object.keys(filter)[0]
    const firstValue = filter[firstKey]
    let paramKey

    switch (firstKey) {
      case 'available':
        params.append('filter.v.availability', Number(firstValue))
        break
      case 'price':
        if (firstValue.min) {
          params.set('filter.v.price.gte', firstValue.min)
        }

        if (firstValue.max) {
          params.set('filter.v.price.lte', firstValue.max)
        }

        break
      case 'productMetafield':
        paramKey = `filter.p.m.${firstValue.namespace}.${firstValue.key}`
        params.append(paramKey, firstValue.value)
        break
      case 'productType':
        params.append('filter.p.product_type', firstValue)
        break
      case 'productVendor':
        params.append('filter.p.vendor', firstValue)
        break
      case 'tag':
        params.append('filter.p.tag', firstValue)
        break
      case 'variantMetafield':
        paramKey = `filter.v.m.${firstValue.namespace}.${firstValue.key}`
        params.append(paramKey, firstValue.value)
        break
      case 'variantOption':
        paramKey = `filter.v.option.${firstValue.name}`
        params.append(paramKey, firstValue.value)
        break
      default:
        break
    }
  })
}

/**
 * Toggles the object in the filter array
 * @param {Array} arr - Array to add or remove object from.
 * @param {Object} obj - Object to add or remove from array.
 */
export function toggleObjectFromArray(arr, obj) {
  const index = arr.findIndex(
    (item) => JSON.stringify(item) === JSON.stringify(obj),
  )

  if (index === -1) {
    arr.push(obj)
  } else {
    arr.splice(index, 1)
  }
}
